import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import Form, {
  Item,
  Label,
  ButtonItem,
  ButtonOptions,
  RequiredRule,
  CompareRule
} from 'devextreme-react/form';
import LoadIndicator from 'devextreme-react/load-indicator';
import notify from 'devextreme/ui/notify';
import { authenticationService } from '../../services/AuthService';

import '../login-form/LoginForm.scss';

export default function ResetPasswordForm() {
  const { clientName, employeeNo, token } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [tokenValid, setTokenValid] = useState(false);
  const [tokenReason, setTokenReason] = useState('');
  const formData = useRef({ newPassword: '', confirmPassword: '' });

  const toast = (message, type = 'error') => {
    notify({
      message: message,
      position: { my: 'top center', at: 'top center', of: window, offset: '0 70' }
    }, type, 5000);
  };

  useEffect(() => {
    (async () => {
      try {
        const decodedToken = decodeURIComponent(token || '');
        const result = await authenticationService.validateResetToken(
          decodeURIComponent(clientName || ''),
          parseInt(employeeNo, 10),
          decodedToken
        );
        setTokenValid(result.isValid ?? result.valid);
        setTokenReason(result.reason || '');
      } catch {
        setTokenValid(false);
        setTokenReason('invalid');
      } finally {
        setLoading(false);
      }
    })();
  }, [clientName, employeeNo, token]);

  const getInvalidMessage = () => {
    switch (tokenReason) {
      case 'expired':
        return 'Lenken har utløpt. Be om en ny tilbakestilling av passord.';
      case 'used':
        return 'Lenken er allerede brukt. Be om en ny tilbakestilling av passord.';
      default:
        return 'Lenken er ugyldig. Be om en ny tilbakestilling av passord.';
    }
  };

  const onSubmit = useCallback(async (e) => {
    e.preventDefault();
    const { newPassword, confirmPassword } = formData.current;
    if (newPassword.length < 8) {
      toast('Passordet må være minst 8 tegn');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast('Passordene er ikke like');
      return;
    }

    setSubmitting(true);
    try {
      const decodedToken = decodeURIComponent(token || '');
      const response = await authenticationService.resetPassword(
        decodeURIComponent(clientName || ''),
        parseInt(employeeNo, 10),
        decodedToken,
        newPassword,
        confirmPassword
      );
      toast(response.message || 'Passordet er oppdatert.', 'success');
      navigate('/login');
    } catch (err) {
      toast(err.message || 'Noe gikk galt. Vær så snill, prøv på nytt.');
    } finally {
      setSubmitting(false);
    }
  }, [clientName, employeeNo, token, navigate]);

  if (loading) {
    return (
      <div className="login-form" style={{ textAlign: 'center' }}>
        <LoadIndicator height={32} width={32} indicatorSrc="loading-buffering.gif" />
      </div>
    );
  }

  if (!tokenValid) {
    return (
      <div className="login-form">
        <p className="form-text">{getInvalidMessage()}</p>
        <div className="link">
          <Link to="/forgot-password">Be om ny tilbakestilling</Link>
        </div>
        <div className="link">
          <Link to="/login">Tilbake til innlogging</Link>
        </div>
      </div>
    );
  }

  return (
    <form className="login-form" onSubmit={onSubmit}>
      <p className="form-text">Velg et nytt passord (minst 8 tegn).</p>
      <Form formData={formData.current} disabled={submitting}>
        <Item
          dataField="newPassword"
          editorType="dxTextBox"
          editorOptions={passwordEditorOptions}
        >
          <RequiredRule message="Passord er påkrevd" />
          <Label visible={false} />
        </Item>
        <Item
          dataField="confirmPassword"
          editorType="dxTextBox"
          editorOptions={passwordEditorOptions}
        >
          <RequiredRule message="Bekreft passord er påkrevd" />
          <CompareRule
            comparisonTarget={() => formData.current.newPassword}
            message="Passordene er ikke like"
          />
          <Label visible={false} />
        </Item>
        <ButtonItem>
          <ButtonOptions width="100%" type="default" useSubmitBehavior={true}>
            <span className="dx-button-text">
              {submitting
                ? <LoadIndicator height={24} width={24} indicatorSrc="loading-buffering.gif" />
                : 'Oppdater passord'}
            </span>
          </ButtonOptions>
        </ButtonItem>
      </Form>
    </form>
  );
}

const passwordEditorOptions = { stylingMode: 'filled', placeholder: 'Passord', mode: 'password' };
